// @ts-nocheck
import { useRef, useState } from "react";
import AccessTimeIcon from "@material-ui/icons/AccessTime";
import AttachFileIcon from "@material-ui/icons/AttachFile";
import CancelIcon from "@material-ui/icons/Cancel";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import ChevronRightOutlinedIcon from "@material-ui/icons/ChevronRightOutlined";
import InsertDriveFileOutlinedIcon from "@material-ui/icons/InsertDriveFileOutlined";
import PhotoCameraOutlinedIcon from "@material-ui/icons/PhotoCameraOutlined";
import SwapHorizIcon from "@material-ui/icons/SwapHoriz";
import { format, parseISO } from "date-fns";

import SeloApostila from "../../SeloApostila";
import { useRequirement } from "../../../hooks/requirement";
import { useToast } from "../../../hooks/toast";
import api from "../../../services/api";
import {
  Acoes,
  Apostila,
  Arquivo,
  BotaoSaibaMais,
  BotaoUpload,
  Item,
  Motivo,
  Status,
} from "./styles";

const ACEITA_ARQUIVO = ".pdf,.jpg,.jpeg,.png,.webp,.heic,application/pdf,image/*";
const TAMANHO_MAXIMO_MB = 12;

// Estado do item pelo arquivo enviado (não pelo status físico do documento).
function estadoDoItem(arquivo, uploadAberto) {
  if (!arquivo) {
    return {
      bgColor: "#FFF8E1",
      iconColor: "#EE9E00",
      color: "#42455F",
      icon: <AccessTimeIcon />,
      texto: uploadAberto ? "Aguardando envio" : "Não enviado",
    };
  }
  if (arquivo.status === "rejected") {
    return {
      bgColor: "#FEE2E8",
      iconColor: "#DB0A3C",
      color: "#DB0A3C",
      icon: <CancelIcon />,
      texto: "Recusado: envie de novo",
    };
  }
  if (arquivo.status === "approved") {
    return {
      bgColor: "#E5FFEB",
      iconColor: "#1F9833",
      color: "#2E5D38",
      icon: <CheckCircleIcon />,
      texto: "Aprovado na conferência",
    };
  }
  return {
    bgColor: "#E5FFEB",
    iconColor: "#1F9833",
    color: "#2E5D38",
    icon: <CheckCircleIcon />,
    texto: uploadAberto ? "Enviado" : "Enviado · em conferência",
  };
}

const tamanhoLegivel = (bytes) => {
  if (!bytes) return "";
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1).replace(".", ",")} MB`;
};

export default function DocumentoAnalise({
  processo,
  documento,
  uploadAberto,
  onProcessoAtualizado,
  controllerModal,
  FMDModal,
}) {
  const { addToast } = useToast();
  const { addRequirement } = useRequirement();
  const [enviando, setEnviando] = useState(false);
  const [salvandoApostila, setSalvandoApostila] = useState(false);
  const [apostiladoPendente, setApostiladoPendente] = useState(false);
  const inputFotoRef = useRef(null);
  const inputArquivoRef = useRef(null);

  const arquivo = documento?.arquivo || null;
  const estado = estadoDoItem(arquivo, uploadAberto);
  const familyPlan = processo?.sale?.family_plan;
  const requerente = processo?.applicant?.name || "";
  const rota = `central/processes/${processo?.id}/analise/documents/${documento?.document_id}/file`;

  // Pedido de emissão pela CJ (mesmo fluxo do 2026: custas do cartório por
  // conta do cliente). Só enquanto o documento ainda não tem arquivo.
  const podePedirEmissao =
    uploadAberto && !arquivo && processo?.sale?.is_fmd && documento?.enabled_to_fmd === 1;

  const limparInputs = () => {
    if (inputFotoRef.current) inputFotoRef.current.value = "";
    if (inputArquivoRef.current) inputArquivoRef.current.value = "";
  };

  const enviar = async (event) => {
    const file = event.target.files && event.target.files[0];
    if (!file) return;

    if (file.size > TAMANHO_MAXIMO_MB * 1024 * 1024) {
      addToast({
        type: "error",
        title: "Arquivo muito grande",
        description: `Envie um arquivo de até ${TAMANHO_MAXIMO_MB} MB.`,
      });
      limparInputs();
      return;
    }

    const formData = new FormData();
    formData.append("file", file);
    formData.append("apostilado", arquivo ? (arquivo.apostilado ? "1" : "0") : apostiladoPendente ? "1" : "0");
    setEnviando(true);

    try {
      const response = await api.post(rota, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const atualizado = response?.data?.data || response?.data;
      onProcessoAtualizado && onProcessoAtualizado(atualizado);

      const fechou = atualizado?.step?.name === "analise_em_andamento";
      addToast({
        type: "success",
        title: fechou ? "Lista completa: análise iniciada" : arquivo ? "Arquivo trocado" : "Documento enviado",
        description: fechou
          ? "Recebemos todos os documentos. O laudo sai em até 7 dias."
          : "Você pode trocar o arquivo até a lista ficar completa.",
      });
    } catch (err) {
      addToast({
        type: "error",
        title: "Não foi possível enviar",
        description:
          err?.response?.data?.message ||
          "Tente de novo em instantes. Se continuar, fale com o nosso suporte.",
      });
    } finally {
      setEnviando(false);
      limparInputs();
    }
  };

  const marcarApostilado = async (event) => {
    const marcado = event.target.checked;

    if (!arquivo) {
      // Sem arquivo ainda: a marcação vai junto com o upload.
      setApostiladoPendente(marcado);
      return;
    }

    setSalvandoApostila(true);
    try {
      const response = await api.patch(rota, { apostilado: marcado ? "1" : "0" });
      const atualizado = response?.data?.data || response?.data;
      onProcessoAtualizado && onProcessoAtualizado(atualizado);
    } catch (err) {
      addToast({
        type: "error",
        title: "Não foi possível salvar",
        description: err?.response?.data?.message || "Tente de novo em instantes.",
      });
    } finally {
      setSalvandoApostila(false);
    }
  };

  // O arquivo só sai por rota autenticada: baixa com o token e abre em
  // outra aba (um <a href> não levaria o Authorization).
  const abrirArquivo = async () => {
    if (!arquivo?.id) return;
    try {
      const response = await api.get(`central/analise/files/${arquivo.id}`, {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(response.data);
      window.open(url, "_blank", "noopener");
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (err) {
      addToast({
        type: "error",
        title: "Não foi possível abrir o arquivo",
        description: "Tente de novo em instantes.",
      });
    }
  };

  const apostiladoMarcado = arquivo ? !!arquivo.apostilado : apostiladoPendente;

  return (
    <Item key={documento?.document_id} data-testid={`analise-doc-${documento?.document_id}`}>
      <Status color={estado.color} iconColor={estado.iconColor} bgColor={estado.bgColor}>
        {estado.icon}
        <p>{estado.texto}</p>
      </Status>

      <p className="title">
        {documento?.name}
        <SeloApostila nome={documento?.name} familyPlan={familyPlan} />
      </p>
      <p className="description">
        {documento?.format} {documento?.name}
        {documento?.from_who && " "}
        {documento?.from_who && documento.from_who.replace("applicant", requerente)}
        {documento?.specification && ", "}
        {documento?.specification}
      </p>

      {arquivo?.status === "rejected" && (
        <Motivo>
          <strong>Por que foi recusado</strong>
          {arquivo.rejection_reason || "O arquivo não atende às especificações do documento."}
        </Motivo>
      )}

      {arquivo && (
        <Arquivo>
          <div className="linha">
            <InsertDriveFileOutlinedIcon />
            <button type="button" className="abrir" onClick={abrirArquivo} title="Abrir o arquivo enviado">
              {arquivo.name || "Arquivo enviado"}
            </button>
          </div>
          <p className="meta">
            {arquivo.uploaded_at && `Enviado em ${format(parseISO(arquivo.uploaded_at), "dd/MM/yyyy 'às' HH:mm")}`}
            {arquivo.size ? ` · ${tamanhoLegivel(arquivo.size)}` : ""}
            {arquivo.apostilado ? " · já apostilado" : " · ainda sem apostila"}
          </p>
        </Arquivo>
      )}

      <Apostila aria-disabled={!uploadAberto || salvandoApostila}>
        <input
          type="checkbox"
          checked={apostiladoMarcado}
          disabled={!uploadAberto || salvandoApostila}
          onChange={marcarApostilado}
          data-testid={`apostilado-${documento?.document_id}`}
        />
        Este documento já está apostilado
      </Apostila>

      {uploadAberto && (
        <>
          <Acoes>
            <BotaoUpload className="camera" primario={!arquivo} aria-disabled={enviando}>
              <PhotoCameraOutlinedIcon />
              {enviando ? "Enviando..." : arquivo ? "Tirar outra foto" : "Tirar foto"}
              <input
                ref={inputFotoRef}
                type="file"
                accept="image/*"
                capture="environment"
                disabled={enviando}
                onChange={enviar}
              />
            </BotaoUpload>
            <BotaoUpload primario={!arquivo} aria-disabled={enviando}>
              {arquivo ? <SwapHorizIcon /> : <AttachFileIcon />}
              {enviando ? "Enviando..." : arquivo ? "Trocar arquivo" : "Enviar foto ou PDF"}
              <input
                ref={inputArquivoRef}
                type="file"
                accept={ACEITA_ARQUIVO}
                disabled={enviando}
                onChange={enviar}
                data-testid={`input-analise-${documento?.document_id}`}
              />
            </BotaoUpload>
          </Acoes>
          <p className="ajuda">
            Foto nítida, com o documento inteiro e sem cortes (frente e verso quando houver), ou o PDF. Até {TAMANHO_MAXIMO_MB} MB.
          </p>
        </>
      )}

      {podePedirEmissao && (
        <BotaoSaibaMais
          type="button"
          className="black"
          onClick={() => {
            addRequirement({ ...documento, applicant: requerente, identifier: processo?.identifier, service_code: processo?.service_code, type: processo?.type, family_plan: familyPlan, is_fmd: processo?.sale?.is_fmd, emission_paid_by_client: processo?.sale?.emission_paid_by_client });
            FMDModal && FMDModal();
          }}
        >
          <span>Pedir a emissão pela CJ (custas do cartório por sua conta)</span>
          <ChevronRightOutlinedIcon />
        </BotaoSaibaMais>
      )}

      {documento?.emission?.status && (
        <p className="ajuda">Pedido de emissão: {documento.emission.status}.</p>
      )}

      <BotaoSaibaMais
        type="button"
        onClick={() => {
          addRequirement({ ...documento, applicant: requerente, identifier: processo?.identifier, service_code: processo?.service_code, type: processo?.type, family_plan: familyPlan });
          controllerModal && controllerModal();
        }}
      >
        <span>Saiba mais sobre o formato e como emitir</span>
        <ChevronRightOutlinedIcon />
      </BotaoSaibaMais>
    </Item>
  );
}
